import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { RagBadge, DocStatusBadge } from "@/components/StatusBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Check, X, RefreshCw, Ban, Play, Eye, FileText } from "lucide-react";

const PROFILE_LABELS = {
  legal_business_name: "Legal business name", trading_name: "Trading name", abn: "ABN", acn: "ACN",
  business_address: "Business address", contact_person: "Contact person", phone: "Phone",
  contact_email: "Contact email", bank_account_name: "Bank account name", bank_bsb: "BSB",
  bank_account_number: "Account number", services_offered: "Services offered",
  operating_locations: "Operating locations", vehicle_types: "Vehicle types",
};

function ReviewDialog({ doc, open, onClose, onReviewed }) {
  const [action, setAction] = useState("approve");
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setAction("approve"); setComment(""); }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/admin/documents/${doc.id}`, { action, comment });
      toast.success(`Document ${action === "approve" ? "approved" : action === "reject" ? "rejected" : "marked for replacement"}`);
      onReviewed();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!doc) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid={`review-dialog-${doc.doc_type}`}>
        <DialogHeader>
          <DialogTitle className="font-heading">Review: {doc.doc_label}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-3 gap-2">
            {[
              { key: "approve", label: "Approve", icon: Check, cls: "border-emerald-300 bg-emerald-50 text-emerald-700" },
              { key: "reject", label: "Reject", icon: X, cls: "border-red-300 bg-red-50 text-red-700" },
              { key: "request_replacement", label: "Replace", icon: RefreshCw, cls: "border-orange-300 bg-orange-50 text-orange-700" },
            ].map(({ key, label, icon: Icon, cls }) => (
              <button key={key} type="button" data-testid={`review-action-${key}`}
                onClick={() => setAction(key)}
                className={`flex flex-col items-center gap-1 rounded-md border px-2 py-3 text-xs font-semibold transition-colors duration-150 ${action === key ? cls + " ring-2 ring-offset-1 ring-navy" : "border-border text-muted-foreground hover:bg-slate-50"}`}>
                <Icon className="h-4 w-4" /> {label}
              </button>
            ))}
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium" htmlFor="review-comment">
              Admin comment {action !== "approve" && <span className="text-brand">*</span>}
            </label>
            <Textarea id="review-comment" data-testid="review-comment-input" rows={3}
              placeholder={action === "approve" ? "Optional note…" : "Explain what needs fixing…"}
              value={comment} onChange={(e) => setComment(e.target.value)} />
          </div>
          <Button data-testid="review-submit-button" type="submit" disabled={saving}
            className="w-full bg-brand font-semibold text-white hover:bg-brand-dark">
            {saving ? "Saving…" : "Submit review"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminCarrierDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [reviewDoc, setReviewDoc] = useState(null);
  const [suspending, setSuspending] = useState(false);

  const load = () => {
    api.get(`/admin/subcontractors/${id}`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(load, [id]);

  const toggleSuspend = async () => {
    setSuspending(true);
    const action = data.subcontractor.suspended ? "reactivate" : "suspend";
    const reason = action === "suspend" ? window.prompt("Reason for suspension (optional):") || "" : "";
    try {
      await api.patch(`/admin/subcontractors/${id}/status`, { action, reason });
      toast.success(action === "suspend" ? "Subcontractor suspended" : "Subcontractor reactivated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSuspending(false);
    }
  };

  if (!data) return <div data-testid="carrier-detail-loading" className="text-sm text-muted-foreground">Loading carrier…</div>;

  const { subcontractor, profile, categories, documents, compliance, history } = data;

  return (
    <div className="space-y-6" data-testid="carrier-detail-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Button asChild variant="ghost" size="sm" data-testid="back-to-carriers" className="mb-2 -ml-2 text-muted-foreground">
            <Link to="/admin/carriers"><ArrowLeft className="mr-1 h-4 w-4" /> All carriers</Link>
          </Button>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-heading text-2xl font-extrabold text-navy sm:text-3xl" data-testid="carrier-name">
              {profile.legal_business_name || subcontractor.business_name}
            </h1>
            <RagBadge status={compliance.status} testid="carrier-detail-rag" />
            {subcontractor.suspended && (
              <span data-testid="carrier-suspended-badge" className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-xs font-bold uppercase text-red-700">
                <Ban className="h-3 w-3" /> Suspended
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{subcontractor.email}</p>
        </div>
        <Button data-testid="suspend-toggle-button" onClick={toggleSuspend} disabled={suspending}
          variant={subcontractor.suspended ? "default" : "destructive"}
          className={subcontractor.suspended ? "bg-emerald-600 text-white hover:bg-emerald-700" : ""}>
          {subcontractor.suspended ? <><Play className="mr-2 h-4 w-4" /> Reactivate</> : <><Ban className="mr-2 h-4 w-4" /> Suspend</>}
        </Button>
      </div>

      {(compliance.missing.length > 0 || compliance.rejected.length > 0 || compliance.expired.length > 0) && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4" data-testid="carrier-issues">
          <div className="text-sm font-semibold text-red-800">Compliance issues</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {[...compliance.missing.map((x) => `Missing: ${x}`),
              ...compliance.rejected.map((x) => `Rejected: ${x}`),
              ...compliance.expired.map((x) => `Expired: ${x}`)].map((i) => (
              <span key={i} className="rounded-full border border-red-200 bg-white px-2.5 py-0.5 text-xs font-medium text-red-700">{i}</span>
            ))}
          </div>
        </div>
      )}

      <Card data-testid="carrier-profile-card">
        <CardHeader><CardTitle className="text-base">Company profile</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
          {Object.entries(PROFILE_LABELS).map(([key, label]) => (
            <div key={key}>
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
              <div className="text-sm font-medium text-navy" data-testid={`profile-field-${key}`}>{profile[key] || "—"}</div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card data-testid="carrier-documents-card">
        <CardHeader><CardTitle className="text-base">Documents</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-sm" data-testid="carrier-docs-table">
              <thead>
                <tr className="border-b border-border bg-muted/60 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <th className="px-5 py-3">Document</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Issue / Expiry</th>
                  <th className="px-5 py-3">Comment</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((cat) => {
                  const doc = documents[cat.key];
                  return (
                    <tr key={cat.key} data-testid={`admin-doc-row-${cat.key}`} className="border-b border-border last:border-b-0 hover:bg-slate-50">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2 font-medium text-navy">
                          <FileText className="h-4 w-4 shrink-0" /> {cat.label}
                          {!cat.mandatory && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-500">Optional</span>}
                        </div>
                        {doc && <div className="mt-0.5 pl-6 text-xs text-muted-foreground">{doc.filename} · {new Date(doc.uploaded_at).toLocaleDateString("en-AU")}</div>}
                      </td>
                      <td className="px-5 py-3.5"><DocStatusBadge status={doc ? doc.status : "missing"} testid={`admin-doc-status-${cat.key}`} /></td>
                      <td className="px-5 py-3.5 text-xs text-muted-foreground">
                        {doc ? <><div>{doc.issue_date || "—"}</div><div>{doc.expiry_date || "—"}</div></> : "—"}
                      </td>
                      <td className="max-w-[180px] truncate px-5 py-3.5 text-xs text-muted-foreground" title={doc?.admin_comment || ""}>
                        {doc?.admin_comment || "—"}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-end gap-2">
                          {doc && (
                            <>
                              <Button asChild variant="outline" size="sm" data-testid={`admin-doc-view-${cat.key}`}>
                                <a href={`${API_BASE}/documents/${doc.id}/file`} target="_blank" rel="noopener noreferrer">
                                  <Eye className="mr-1 h-3.5 w-3.5" /> View
                                </a>
                              </Button>
                              <Button size="sm" data-testid={`admin-doc-review-${cat.key}`}
                                className="bg-navy text-white hover:bg-navy-light"
                                onClick={() => setReviewDoc(doc)}>
                                Review
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="carrier-history-card">
        <CardHeader><CardTitle className="text-base">Compliance history</CardTitle></CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground">No history yet.</p>
          ) : (
            <ol className="relative space-y-4 border-l border-border pl-5">
              {history.map((h) => (
                <li key={h.id} className="relative" data-testid={`history-item-${h.id}`}>
                  <span className="absolute -left-[26px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-brand bg-white" />
                  <div className="text-sm font-medium text-navy">{h.detail || h.event}</div>
                  <div className="text-xs text-muted-foreground">{new Date(h.timestamp).toLocaleString("en-AU")}</div>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>

      {reviewDoc && (
        <ReviewDialog doc={reviewDoc} open={!!reviewDoc} onClose={() => setReviewDoc(null)} onReviewed={load} />
      )}
    </div>
  );
}
